import { type LicenseData } from "../hooks/useLicenseStore"
import { type LicenseIssue, validateLicenseData } from "./licenseValidation"
import { applySharingRules } from "./applySharingRules"
import { applyPublishingRules } from "./applyPublishingRules"

export type YesNo = "yes" | "no"

export type NormalizedLicenseData = {
  datasetTitle?: string
  datasetDescription?: string
  identifiersList: string[]
  yearGenerated?: string
  hasExpiry: boolean
  expiryDate?: string
  expiryActions?: string
  accessMode: string[]
  commercialUse: YesNo
  onwardSharing: YesNo
  shareAlike: YesNo
  modifications: YesNo
  benefitSharing: YesNo
  benefitSharingContactEmail?: string
  benefitSharingContactUrl?: string
  benefitSharingActivities?: string
  costRecovery: YesNo
  costRecoveryContactEmail?: string
  costRecoveryContactUrl?: string
  costRecoveryDetails?: string
  publishingPermitted: YesNo
  publishingRequirements: Record<string, boolean>
  reportingRequirements: YesNo
  reportingTypesPermitted?: string
  attributionRequired: YesNo
  attributionRequirements: Record<string, boolean>
  logoVariant: "color" | "bw"
  acceptedAuthorization: boolean
  acceptedMutability: boolean
  acceptedTerms: boolean
  generatedDate: string
}

type NormalizeResult = {
  normalizedData: NormalizedLicenseData
  issues: LicenseIssue[]
}

export const normalizeLicenseData = (input: LicenseData): NormalizeResult => {
  input = applyPublishingRules(applySharingRules(input))
  const asString = (value: unknown) =>
    typeof value === "string" ? value.trim() : undefined

  const asYesNo = (value: unknown, fallback: YesNo): YesNo => {
    if (value === "yes" || value === "no") return value
    if (typeof value === "string") {
      const next = value.trim().toLowerCase()
      if (next === "yes" || next === "no") return next
    }
    if (typeof value === "boolean") return value ? "yes" : "no"
    return fallback
  }

  const asRecord = (value: unknown): Record<string, boolean> =>
    value && typeof value === "object" ? (value as Record<string, boolean>) : {}

  const asArray = (value: unknown): string[] =>
    Array.isArray(value)
      ? value.map(item => asString(item)).filter((item): item is string => !!item)
      : []

  const asBool = (value: unknown): boolean => {
    if (typeof value === "boolean") return value
    if (typeof value === "string") {
      const next = value.trim().toLowerCase()
      if (["true", "yes", "1", "on"].includes(next)) return true
      if (["false", "no", "0", "off"].includes(next)) return false
    }
    return false
  }

  const accessModeRaw = input.accessMode
  const accessMode =
    accessModeRaw && typeof accessModeRaw === "object"
      ? Object.entries(accessModeRaw)
          .filter(([, value]) => Boolean(value))
          .map(([key]) => key)
      : []

  const normalized: NormalizedLicenseData = {
    datasetTitle: asString(input.datasetTitle),
    datasetDescription: asString(input.datasetDescription),
    identifiersList: asArray(input.identifiersList),
    yearGenerated: asString(input.yearGenerated),
    hasExpiry: asYesNo(input.hasExpiry, "no") === "yes",
    expiryDate: asString(input.expiryDate),
    expiryActions: asString(input.expiryActions),
    accessMode,
    commercialUse: asYesNo(input.commercialUse, "yes"),
    onwardSharing: asYesNo(input.onwardSharing, "yes"),
    shareAlike: asYesNo(input.shareAlike, "no"),
    modifications: asYesNo(input.modifications, "yes"),
    benefitSharing: asYesNo(input.benefitSharing, "no"),
    benefitSharingContactEmail: asString(input.benefitSharingContactEmail),
    benefitSharingContactUrl: asString(input.benefitSharingContactUrl),
    benefitSharingActivities: asString(input.benefitSharingActivities),
    costRecovery: asYesNo(input.costRecovery, "no"),
    costRecoveryContactEmail: asString(input.costRecoveryContactEmail),
    costRecoveryContactUrl: asString(input.costRecoveryContactUrl),
    costRecoveryDetails: asString(input.costRecoveryDetails),
    publishingPermitted: asYesNo(input.publishingPermitted, "yes"),
    publishingRequirements: asRecord(input.publishingRequirements),
    reportingRequirements: asYesNo(input.reportingRequirements, "no"),
    reportingTypesPermitted: asString(input.reportingTypesPermitted),
    attributionRequired: asYesNo(input.attributionRequired, "no"),
    attributionRequirements: asRecord(input.attributionRequirements),
    logoVariant: asString(input.logoVariant) === "bw" ? "bw" : "color",
    acceptedAuthorization: asBool(input.acceptedAuthorization),
    acceptedMutability: asBool(input.acceptedMutability),
    acceptedTerms: asBool(input.acceptedTerms),
    generatedDate: new Date().toISOString().slice(0, 10)
  }

  return {
    normalizedData: normalized,
    issues: validateLicenseData(input, normalized)
  }
}
